import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft, MapPin, Calendar, Weight, Truck, DollarSign, 
  Clock, AlertCircle, CheckCircle, Send, Package, Loader2, Upload, Phone, MessageCircle
} from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { bidService } from '../services/bidService';
import { notificationService } from '../services/notificationService';
import { driverService } from '../services/driverService';
import { useAppAlert } from '../components/AppAlertProvider';

interface OfferRequest {
  id: string;
  shipmentId: string;
  internalShipmentId?: string;
  shipmentWorkiomRecordId?: string;
  shipmentCode?: string;
  myBidId?: string;
  date: string;
  weight: string;
  origin: string;
  originCity: string;
  originDistrict?: string;
  destination: string;
  destinationCity: string;
  deliveryPointsText?: string;
  deliveryPoints?: string[];
  deliveryPointsDetailed?: Array<{ label: string; city?: string; district?: string }>;
  currentBestOffer?: number;
  minimumBidAmount?: number;
  myOffer?: number;
  status: 'pending' | 'evaluated' | 'approved' | 'rejected';
  offerCount?: number;
  deadline?: string;
  distance?: string;
  loadType?: string;
  vehicleType?: string;
  vehicleSpecType?: string;
  palletCount?: number;
  vehicleCount?: number;
  stopCount?: number;
  volume?: number;
  requireReferenceWaybill?: boolean;
  phoneOnlyQuote?: boolean;
  loadingContactPhone?: string;
  shipmentContactPhone?: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
}

const phoneNumberFormat = (value: string) => {
  const normalized = String(value || '').replace(/\D/g, '').replace(/^90/, '').replace(/^0/, '');
  return normalized;
};

export function DriverMobileOfferDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert, showConfirm } = useAppAlert();
  const [user, setUser] = useState<any>(null);
  const [offer, setOffer] = useState<OfferRequest | null>(null);
  const [bidAmount, setBidAmount] = useState('');
  const [showBidForm, setShowBidForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [referenceWaybillFile, setReferenceWaybillFile] = useState<File | null>(null);
  const [isNewDriver, setIsNewDriver] = useState(false);
  const [accessWarning, setAccessWarning] = useState('');

  const whatsappInquiryMessage = offer ? encodeURIComponent([
    `Merhaba, ${offer.shipmentId || offer.shipmentCode} numaralı sevkiyat için görüşmek istiyordum.`,
    `Yükleme: ${offer.origin || '-'} / ${offer.originCity || '-'}`,
    `Boşaltma: ${offer.destination || '-'} / ${offer.destinationCity || '-'}`,
    `Tarih: ${offer.date || '-'}`,
    `Tonaj: ${offer.weight || '-'} ton`,
    `Palet: ${offer.palletCount ?? 0}`,
    `Araç Türü: ${offer.vehicleSpecType || offer.vehicleType || '-'}`,
    '',
    `Sürücü: ${user?.name || user?.full_name || user?.fullName || '-'}`,
    `Telefon: +90 ${phoneNumberFormat(user?.phone || '')}`,
  ].join('\n')) : '';

  const parsedBidAmount = bidAmount ? parseFloat(bidAmount) : 0;
  const bidAmountError = !bidAmount
    ? ''
    : offer?.currentBestOffer && parsedBidAmount >= offer.currentBestOffer
        ? `Teklifiniz mevcut en dusuk teklif olan ${offer.currentBestOffer.toLocaleString('tr-TR')} TL tutarinin altinda olmalidir.`
        : '';

  useEffect(() => {
    const initialize = async () => {
      const userData = sessionStorage.getItem('driverMobileUser');
      if (!userData) {
        navigate('/driver-mobile-auth');
        return;
      }
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      try {
        const driverResponse = await driverService.getDriverByUserId(parsedUser.id);
        const driver = driverResponse.data || driverResponse;
        setDriverId(String(driver?.id || ''));
        setIsNewDriver(Number(driver?.completed_shipments || 0) === 0);
        await loadOffer(parsedUser, String(driver?.id || ''));
      } catch (error) {
        console.error('Failed to resolve driver profile:', error);
        await loadOffer(parsedUser);
      }
    };

    initialize();
  }, [id, navigate]);

  const loadOffer = async (currentUser?: any, currentDriverId?: string) => {
    try {
      setLoading(true);
      const response = await bidService.getBidRequest(String(id));
      const foundBid = response.data || response;
      if (foundBid) {
        const bidsResponse = await bidService.getAll({ shipmentId: String(foundBid.shipment_id || foundBid.id) });
        const bids = (bidsResponse.data || bidsResponse) as any[];
        const myBid = bids.find((bid: any) => String(bid.driver_id) === String(currentDriverId || currentUser?.driverId) && bid.status !== 'rejected');
        const convertedOffer: OfferRequest = {
          id: String(foundBid.id || foundBid.bid_request_id),
          internalShipmentId: String(foundBid.shipment_id || foundBid.id),
          shipmentWorkiomRecordId: foundBid.workiom_record_id ? String(foundBid.workiom_record_id) : (foundBid.shipment_workiom_record_id ? String(foundBid.shipment_workiom_record_id) : undefined),
          shipmentId: String(foundBid.shipment_workiom_record_id || foundBid.workiom_record_id || foundBid.shipment_id || foundBid.id),
          shipmentCode: String(foundBid.shipment_no || foundBid.sevk_kodu || foundBid.shipment_id || foundBid.id),
          date: new Date(foundBid.deadline || foundBid.valid_until || Date.now()).toLocaleDateString('tr-TR'),
          weight: String(foundBid.weight || foundBid.cargo_weight || foundBid.loadDetails?.weight || '0'),
          origin: foundBid.origin?.name || foundBid.loading_point || 'Yukleme Noktasi',
          originCity: foundBid.origin?.city || foundBid.loading_city || '',
          originDistrict: foundBid.loading_district || foundBid.pickup_district || '',
          destination: foundBid.destination?.name || foundBid.delivery_point || 'Teslimat Noktasi',
          destinationCity: foundBid.destination?.city || foundBid.delivery_city || '',
          deliveryPointsText: foundBid.delivery_points_text || '',
          deliveryPoints: Array.isArray(foundBid.delivery_points) ? foundBid.delivery_points : [],
          deliveryPointsDetailed: Array.isArray(foundBid.delivery_points_detailed) ? foundBid.delivery_points_detailed : [],
          currentBestOffer: foundBid.lowestBid?.amount || foundBid.current_lowest_bid,
          minimumBidAmount: Number(foundBid.minimum_bid || foundBid.minimumBid || 0) || undefined,
          myBidId: myBid?.id ? String(myBid.id) : undefined,
          myOffer: myBid?.bid_amount || myBid?.amount,
          offerCount: foundBid.totalBids || foundBid.offer_count || bids.length || 0,
          status: foundBid.status === 'open' ? 'pending' : foundBid.status === 'awarded' ? 'approved' : foundBid.status === 'closed' ? 'evaluated' : 'rejected',
          deadline: new Date(foundBid.deadline || foundBid.valid_until || Date.now()).toLocaleString('tr-TR'),
          distance: foundBid.distance_km !== undefined && foundBid.distance_km !== null
            ? `${foundBid.distance_km} km`
            : foundBid.distance
              ? String(foundBid.distance)
              : foundBid.loadDetails?.distance
                ? String(foundBid.loadDetails.distance)
                : 'Belirtilmemis',
          loadType: foundBid.loadDetails?.type || foundBid.cargo_type || 'Genel Kargo',
          vehicleType: foundBid.vehicleRequirements?.type || foundBid.vehicle_type || 'Tenteli',
          vehicleSpecType: foundBid.vehicle_spec_type || foundBid.vehicle_type || '-',
          palletCount: Number(foundBid.pallet_count || 0),
          vehicleCount: Number(foundBid.vehicle_count || 1),
          stopCount: Number(foundBid.stop_count || 1),
          volume: Number(foundBid.cargo_volume || 0),
          requireReferenceWaybill: Boolean(foundBid.require_reference_waybill || foundBid.requireReferenceWaybill),
          phoneOnlyQuote: Boolean(foundBid.phone_only_quote || foundBid.phoneOnlyQuote),
          loadingContactPhone: foundBid.loading_contact_phone || foundBid.loadingContactPhone || undefined,
          shipmentContactPhone: foundBid.shipment_contact_phone || foundBid.shipmentContactPhone || undefined,
          pickupLatitude: foundBid.pickup_latitude ? Number(foundBid.pickup_latitude) : undefined,
          pickupLongitude: foundBid.pickup_longitude ? Number(foundBid.pickup_longitude) : undefined,
          deliveryLatitude: foundBid.delivery_latitude ? Number(foundBid.delivery_latitude) : undefined,
          deliveryLongitude: foundBid.delivery_longitude ? Number(foundBid.delivery_longitude) : undefined,
        };
        setOffer(convertedOffer);
      }
    } catch (error) {
      console.error('Failed to load offer:', error);
      setAccessWarning((error as any)?.message || 'Teklif detayi yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitBid = async () => {
    if (!bidAmount || parseFloat(bidAmount) <= 0) {
      showAlert({ tone: 'warning', title: 'Geçersiz Tutar', message: 'Lütfen geçerli bir teklif miktarı giriniz.' });
      return;
    }

    if (!driverId) {
      showAlert({ tone: 'error', title: 'Sürücü Bulunamadı', message: 'Sürücü profili bulunamadı. Lütfen tekrar giriş yapın.' });
      return;
    }

    if (offer?.requireReferenceWaybill && isNewDriver && !referenceWaybillFile) {
      showAlert({ tone: 'warning', title: 'Referans Belge Gerekli', message: 'Bu taşımada teklif verebilmek için önce referans e-irsaliye yüklemelisiniz.' });
      return;
    }

    const amount = parseFloat(bidAmount);

    // En düşük tekliften düşük olmalı kontrolü
    if (offer?.currentBestOffer && amount >= offer.currentBestOffer) {
      showAlert({ tone: 'warning', title: 'Teklif Uygun Değil', message: `Teklifiniz mevcut en düşük tekliften (${offer.currentBestOffer.toLocaleString('tr-TR')} TL) düşük olmalıdır.` });
      return;
    }

    try {
      if (offer?.myBidId) {
        await bidService.update(offer.myBidId, {
          bidAmount: amount,
          driverWorkiomId: user?.workiomRecordId || user?.workiom_record_id || undefined,
          tractorPlate: user?.vehiclePlate || user?.cekici_plaka || undefined,
          trailerPlate: user?.trailerPlate || user?.dorse_plaka || undefined,
          vehicleType: user?.vehicleType || user?.vehicle_type || undefined,
        });
      } else {
        if (offer?.requireReferenceWaybill && isNewDriver && referenceWaybillFile) {
          const formData = new FormData();
          formData.append('file', referenceWaybillFile);
          formData.append('entityType', 'driver');
          formData.append('entityId', String(user.id));
          formData.append('documentType', 'reference-waybill');
          formData.append('name', referenceWaybillFile.name);
          await bidService.uploadReferenceWaybill(formData);
        }

        await bidService.create({
          shipmentId: String(offer?.internalShipmentId || offer?.shipmentId),
          driverId: String(driverId),
          bidAmount: amount,
          driverWorkiomId: user?.workiomRecordId || user?.workiom_record_id || undefined,
          tractorPlate: user?.vehiclePlate || user?.cekici_plaka || undefined,
          trailerPlate: user?.trailerPlate || user?.dorse_plaka || undefined,
          vehicleType: user?.vehicleType || user?.vehicle_type || undefined,
        });
      }
      try {
        await notificationService.create({
          userId: user.id,
          type: 'bid',
          title: offer?.myBidId ? 'Teklif Guncellendi' : 'Teklif Gonderildi',
          message: `${offer?.shipmentCode || offer?.shipmentId} numarali sevkiyat icin ${amount.toLocaleString('tr-TR')} TL teklifiniz ${offer?.myBidId ? 'guncellendi' : 'basariyla gonderildi'}.`,
          relatedId: Number(offer?.id),
          relatedEntityType: 'bid_request',
        });
      } catch (notificationError) {
        console.error('Failed to create bid notification:', notificationError);
      }

      showAlert({
        tone: 'success',
        title: offer?.myBidId ? 'Teklif Güncellendi' : 'Teklif Gönderildi',
        message: offer?.myBidId ? 'Teklifiniz başarıyla güncellendi.' : 'Teklifiniz başarıyla gönderildi.',
      });
      setBidAmount('');
      setShowBidForm(false);
      await loadOffer(user, driverId);
    } catch (error: any) {
      console.error('Bid submit failed:', error);
      showAlert({ tone: 'error', title: 'Teklif Gönderilemedi', message: error?.message || 'Teklif gönderilirken hata oluştu' });
    }
  };

  if (loading || !offer || !user) {
    return (
      <div className="safe-area-page min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-gray-500">{accessWarning || 'Yükleniyor...'}</p>
        </div>
      </div>
    );
  }

  const canBid = offer.status === 'pending';
  const deliveryPointDetails = Array.isArray(offer.deliveryPointsDetailed) && offer.deliveryPointsDetailed.length > 0
    ? offer.deliveryPointsDetailed
    : [{ label: offer.destination, city: offer.destinationCity, district: '' }];

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Teklif Talep Detayı</h1>
          <div className="w-9"></div>
        </div>

        {/* Shipment ID */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center dark:border-white/10 dark:bg-white/10 dark:backdrop-blur-sm">
          <p className="mb-1 text-sm text-slate-500 dark:text-blue-200">Sevkiyat No</p>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">{offer.shipmentCode || offer.shipmentId}</p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-4 space-y-4 pb-28">
        {/* Status Card */}
        {offer.status === 'pending' && (
          <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-blue-900 text-sm">Teklife Açık</p>
              <p className="text-sm text-blue-700">Bu sevkiyat için teklif verebilirsiniz</p>
            </div>
          </div>
        )}

        {offer.requireReferenceWaybill && isNewDriver && (
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-900 text-sm">Referans e-irsaliye gerekli</p>
              <p className="text-sm text-amber-800">Bu talepte daha once tasima tamamlamamis suruculer teklif vermeden once referans e-irsaliye yuklemelidir.</p>
            </div>
          </div>
        )}

        <div className={`${1===1 ? 'bg-sky-50 border-l-4 border-sky-600' : 'bg-slate-50 border border-slate-200'} rounded-lg p-4 flex items-start gap-3`}>
          <Phone className={`w-5 h-5 flex-shrink-0 mt-0.5 ${1===1 ? 'text-sky-600' : 'text-slate-600'}`} />
          <div className="w-full">
            <p className={`font-bold text-sm ${1===1 ? 'text-sky-900' : 'text-slate-900'}`}>Sevkiyat İrtibat Hattı</p>
            <p className={`text-sm ${1===1 ? 'text-sky-700' : 'text-slate-700'}`}>
              {1===1
                ? 'Bu sevkiyatla ilgili irtibat hattını arayabilir veya WhatsApp üzerinden hızlıca mesaj gönderebilirsiniz.'
                : 'Teklif öncesi bu sevkiyatla ilgili irtibat hattına ulaşabilirsiniz.'}
            </p>
            <p className={`text-xs mt-2 ${1===1 ? 'text-sky-700' : 'text-slate-600'}`}>İletişim: {offer.shipmentContactPhone || offer.loadingContactPhone || 'Telefon bilgisi yok'}</p>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <a
                href={offer.shipmentContactPhone || offer.loadingContactPhone ? `tel:${offer.shipmentContactPhone || offer.loadingContactPhone}` : '#'}
                className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium ${(offer.shipmentContactPhone || offer.loadingContactPhone) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 pointer-events-none'}`}
              >
                <Phone className="w-4 h-4" />
                Ara
              </a>
              <a
                href={offer.shipmentContactPhone || offer.loadingContactPhone ? `https://wa.me/${String(offer.shipmentContactPhone || offer.loadingContactPhone).replace(/\D/g, '')}?text=${whatsappInquiryMessage}` : '#'}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium ${(offer.shipmentContactPhone || offer.loadingContactPhone) ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-500 pointer-events-none'}`}
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Current Best Offer */}
        {!offer.phoneOnlyQuote && offer.currentBestOffer && (
          <div className="bg-green-50 border-l-4 border-green-600 rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {offer.currentBestOffer && (
                <div>
                  <p className="text-xs text-green-700 mb-1">En Düşük Teklif</p>
                  <p className="text-xl font-bold text-green-900">
                    {offer.currentBestOffer.toLocaleString('tr-TR')} TL
                  </p>
                </div>
              )}
              <div className="text-left sm:text-right">
                <p className="text-xs text-green-700 mb-1">Toplam Teklif</p>
                <p className="text-xl font-bold text-green-900">{offer.offerCount}</p>
              </div>
            </div>
          </div>
        )}

        {/* Route Information */}
        <div className="bg-white rounded-xl shadow-lg p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Güzergah Bilgisi</h3>
          
          <div className="space-y-4">
            {/* Origin */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-green-600 rounded-full"></div>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Yükleme Noktası</p>
                <p className="font-bold text-gray-900">{offer.origin}</p>
                <p className="text-sm text-gray-600">{[offer.originCity, offer.originDistrict].filter(Boolean).join(' / ') || '-'}</p>
                <a href={offer.pickupLatitude && offer.pickupLongitude ? `https://www.google.com/maps/dir/?api=1&destination=${offer.pickupLatitude},${offer.pickupLongitude}` : '#'} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-medium mt-1 inline-block">Yol Tarifi Al</a>
              </div>
            </div>

            {/* Distance */}
            {offer.distance && (
              <div className="ml-5 pl-5 border-l-2 border-dashed border-gray-300">
                <p className="text-sm text-gray-600">
                  📏 Mesafe: <span className="font-bold">{offer.distance || '-'}</span>
                </p>
              </div>
            )}

            {/* Destination */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-red-600 rounded-full"></div>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Teslimat Noktası</p>
                <p className="font-bold text-gray-900">{offer.destination}</p>
                <p className="text-sm text-gray-600">{offer.destinationCity || '-'}</p>
                <a href={offer.deliveryLatitude && offer.deliveryLongitude ? `https://www.google.com/maps/dir/?api=1&destination=${offer.deliveryLatitude},${offer.deliveryLongitude}` : '#'} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-medium mt-1 inline-block">Yol Tarifi Al</a>
              </div>
            </div>

            <div className="ml-5 pl-5 border-l-2 border-dashed border-gray-300 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Teslim Noktalari</p>
              {deliveryPointDetails.map((point, index) => (
                <div key={`${point.label}-${index}`} className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-sm font-semibold text-slate-900">{point.label || `Teslim ${index + 1}`}</p>
                  <p className="text-xs text-slate-600 mt-1">{[point.city, point.district].filter(Boolean).join(' / ') || '-'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Load Details */}
        <div className="bg-white rounded-xl shadow-lg p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Yük Detayları</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Weight className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Ağırlık</p>
                <p className="text-sm font-bold text-gray-900">{offer.weight} ton</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Package className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Yük Tipi</p>
                <p className="text-sm font-bold text-gray-900">{offer.loadType}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Truck className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Araç Türü</p>
                <p className="text-sm font-bold text-gray-900">{offer.vehicleSpecType}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Son Teklif</p>
                <p className="text-sm font-bold text-gray-900">{offer.date}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Package className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Palet Sayısı</p>
                <p className="text-sm font-bold text-gray-900">{offer.palletCount ?? 0}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Truck className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Araç Sayısı</p>
                <p className="text-sm font-bold text-gray-900">{offer.vehicleCount ?? 1}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <MapPin className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Uğrama Sayısı</p>
                <p className="text-sm font-bold text-gray-900">{offer.stopCount ?? 1}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Package className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Yük Hacmi</p>
                <p className="text-sm font-bold text-gray-900">{offer.volume ?? 0} m³</p>
              </div>
            </div>
          </div>
        </div>

        {/* My Offer */}
        {offer.myOffer && (
          <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4">
            <p className="text-xs text-blue-700 mb-1">Verdiğiniz Teklif</p>
            <p className="text-2xl font-bold text-blue-900">
              {offer.myOffer.toLocaleString('tr-TR')} TL
            </p>
          </div>
        )}
      </div>

      {/* Bottom Bid Form */}
      {canBid && (
        <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4">
          {offer.phoneOnlyQuote ? (
            <div className="space-y-3">
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-sky-900">Bu taşımada teklif telefon / WhatsApp üzerinden alınır</p>
                <p className="text-xs text-sky-700 mt-1">Teklif tutarı girmek yerine yükleme noktasını arayabilir veya WhatsApp üzerinden teklif iletebilirsiniz.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={offer.shipmentContactPhone || offer.loadingContactPhone ? `tel:${offer.shipmentContactPhone || offer.loadingContactPhone}` : '#'}
                  className={`inline-flex items-center justify-center gap-2 py-3 rounded-lg font-bold transition-colors ${(offer.shipmentContactPhone || offer.loadingContactPhone) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 pointer-events-none'}`}
                >
                  <Phone className="w-5 h-5" />
                  Teklif İçin Ara
                </a>
                <a
                  href={offer.shipmentContactPhone || offer.loadingContactPhone ? `https://wa.me/${String(offer.shipmentContactPhone || offer.loadingContactPhone).replace(/\D/g, '')}?text=${whatsappInquiryMessage}` : '#'}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex items-center justify-center gap-2 py-3 rounded-lg font-bold transition-colors ${(offer.shipmentContactPhone || offer.loadingContactPhone) ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-500 pointer-events-none'}`}
                >
                  <MessageCircle className="w-5 h-5" />
                  Mesajla Teklif Ver
                </a>
              </div>
              {!(offer.shipmentContactPhone || offer.loadingContactPhone) && <p className="text-xs text-red-600">Sevkiyat için irtibat numarası tanımlı değil.</p>}
            </div>
          ) : !showBidForm ? (
            <button
              onClick={() => {
                setBidAmount(offer.myOffer ? String(offer.myOffer) : '');
                setShowBidForm(true);
              }}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
            >
              <DollarSign className="w-5 h-5" />
              {offer.myOffer ? 'Teklifimi Guncelle' : 'Teklif Ver'}
            </button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teklif Miktarı (TL)
                </label>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder={offer.currentBestOffer ? `${offer.currentBestOffer} TL'den dusuk` : 'Teklif miktari'}
                  min={0}
                  step="0.01"
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-bold ${bidAmountError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                />
                {bidAmountError ? (
                  <p className="text-xs text-red-600 mt-1">{bidAmountError}</p>
                ) : null}
                {offer.currentBestOffer && (
                  <p className="text-xs text-gray-500 mt-1">
                    Mevcut en dusuk teklif: {offer.currentBestOffer.toLocaleString('tr-TR')} TL
                  </p>
                )}

                {offer.requireReferenceWaybill && isNewDriver && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Referans E-Irsaliye</label>
                    <label className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-xl px-4 py-4 flex items-center justify-center gap-2 text-sm text-amber-800 cursor-pointer">
                      <Upload className="w-4 h-4" />
                      <span>{referenceWaybillFile ? referenceWaybillFile.name : 'PDF veya gorsel secin'}</span>
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setReferenceWaybillFile(e.target.files?.[0] || null)} />
                    </label>
                    <p className="mt-2 text-xs text-gray-500">Bu belge bir kez yuklendikten sonra sonraki uygun taleplerde tekrar istenmez.</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowBidForm(false);
                    setBidAmount('');
                  }}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleSubmitBid}
                  disabled={Boolean(bidAmountError) || !bidAmount}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                  {offer.myOffer ? 'Guncelle' : 'Gonder'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
